export type BrandRole = "focal" | "competitor";

/** A competitor of the study's brand, as `brandFacts.competitors` hold it. */
export interface BrandCompetitor {
  id: string;
  name: string;
}

export interface BrandRosterEntry {
  label: string;
  brandRole: BrandRole;
  /**
   * A competitor's `brandFacts` id: what its roster rows and answers carry as
   * `brandId` and every reader keys it by. Absent on the brand's own entry,
   * whose role is its identity (one brand per study).
   */
  brandId?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** The study's competitors, in order, as `brandFacts` hold them; blank names dropped. */
export function brandCompetitors(brandFacts: unknown): Array<BrandCompetitor> {
  if (!isRecord(brandFacts) || !Array.isArray(brandFacts.competitors)) return [];
  return brandFacts.competitors
    .filter(isRecord)
    .filter(
      (competitor): competitor is Record<string, unknown> & BrandCompetitor =>
        typeof competitor.id === "string" && typeof competitor.name === "string",
    )
    .map((competitor) => ({ id: competitor.id, name: competitor.name.trim() }))
    .filter((competitor) => competitor.name.length > 0);
}

/**
 * A competitor id no id in `taken` is, added to `taken`: the one minter of the
 * ids code gives a name the user typed into a form (the goal card's intake,
 * the editor's added row). A model writer states its own (`brandFacts`
 * `competitors[].id`): a competitor's current id keeps it, a new one adds one.
 */
export function mintCompetitorId(taken: Set<string>): string {
  let index = 1;
  while (taken.has(`competitor_${index}`)) index += 1;
  const id = `competitor_${index}`;
  taken.add(id);
  return id;
}

/**
 * The study's brand roster, in order: the brand, then each competitor, in
 * the order the study's `brandFacts` keep them. The one composer of the
 * roster (C13-1): a roster grid's rows, a roster list's answers and both
 * questions' `awarenessEntities` are this projection, never a second copy.
 */
export function brandRoster(brandFacts: unknown): Array<BrandRosterEntry> {
  if (!isRecord(brandFacts)) return [];
  const brand =
    typeof brandFacts.brandName === "string" ? brandFacts.brandName.trim() : "";
  return [
    ...(brand ? [{ label: brand, brandRole: "focal" as const }] : []),
    ...brandCompetitors(brandFacts).map((competitor) => ({
      label: competitor.name,
      brandRole: "competitor" as const,
      brandId: competitor.id,
    })),
  ];
}

/**
 * Whether a roster row or answer (`brandRole`, `brandId`) is `entry`: the
 * brand's own by its role, a competitor by its id, never by its label.
 */
export function isRosterEntryOf(item: unknown, entry: BrandRosterEntry): boolean {
  if (!isRecord(item) || item.brandRole !== entry.brandRole) return false;
  return entry.brandRole === "focal" || item.brandId === entry.brandId;
}

const records = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? value.filter(isRecord) : [];

/** A brand grid's rows when the grid is derived from the brand facts, else null. */
function derivedGridRows(response: unknown): Array<Record<string, unknown>> | null {
  const rows = isRecord(response) && isRecord(response.rows) ? response.rows : null;
  return rows?.source === "fixed" && rows.derivedFrom === "brand_roster"
    ? records(rows.rows)
    : null;
}

/** The ids of a roster question's roster items (a roster list's answers but its pinned ones), else null. */
function rosterItemIds(response: unknown): Array<unknown> | null {
  const grid = derivedGridRows(response);
  if (grid) return grid.map((row) => row.id);
  if (!isRecord(response)) return null;
  const source = isRecord(response.optionSource) ? response.optionSource : null;
  return source?.kind === "brand_roster"
    ? records(response.options)
        .filter((option) => option.exclusive !== true && option.keepLast !== true)
        .map((option) => option.id)
    : null;
}

export interface RosterGridEdit {
  /** The study's competitors after the edit: the one list `set_study_context` carries. */
  competitors: Array<BrandCompetitor>;
  /** The edited grids, by stable key: where a cap refusal belongs. */
  editedKeys: Array<string>;
  /**
   * The edited grids whose every change is a brand joining or leaving: their
   * kept rows are as saved, the added rows follow them and are the brands that
   * joined, and each removed row is a competitor that left the study. The Save
   * sends no rows for them; they follow the brands by derivation.
   */
  followedKeys: Array<string>;
}

/**
 * The study's competitors after the edits to its derived brand grids, on the
 * edited copy: the one simulation the editor's Save sends as
 * `set_study_context` and its live check asks the competitor cap of
 * (`competitorCapIssues`). The brand facts own the brands, so a row the user
 * adds to a derived grid (an id the saved grid lacks) is a competitor that
 * joins them, under an id code mints (`mintCompetitorId`), and a competitor
 * row the user removes leaves them, by its `brandId`, when no other question
 * is still derived from them: while one is, the brand stays in the study and
 * only this grid, now the author's own, drops it. A typed name is the user's
 * input, not a stored identity: a row typed with a brand's exact name, or
 * typed twice in one edit, is that one brand, never a second. Null when the
 * edit changes no brand. A roster list's answers are never read as brands: an
 * answer added to a list is the list's own. The grids whose every change is a
 * brand joining or leaving are `followedKeys`: the Save sends the brands and
 * no rows for them.
 */
export function competitorsAfterGridEdit(args: {
  brandFacts: unknown;
  saved: ReadonlyArray<unknown>;
  local: ReadonlyArray<unknown>;
}): RosterGridEdit | null {
  const local = new Map(
    records(args.local).map((question) => [String(question.stableKey), question]),
  );
  const roster = brandRoster(args.brandFacts);
  const current = brandCompetitors(args.brandFacts);
  const typed: Array<string> = [];
  const leaving = new Set<string>();
  const editedKeys: Array<string> = [];
  const grids: Array<{
    key: string;
    before: Array<Record<string, unknown>>;
    after: Array<Record<string, unknown>>;
  }> = [];
  const derived = new Set<string>();
  for (const saved of records(args.saved)) {
    const key = String(saved.stableKey);
    const edited = local.get(key);
    const before = derivedGridRows(saved.response);
    const savedIds = rosterItemIds(saved.response);
    if (savedIds === null || !edited) continue;
    const sameRoster =
      JSON.stringify(rosterItemIds(edited.response)) === JSON.stringify(savedIds);
    if (sameRoster) {
      derived.add(key);
      continue;
    }
    const after = isRecord(edited.response) && isRecord(edited.response.rows) &&
      edited.response.rows.source === "fixed"
      ? records(edited.response.rows.rows)
      : null;
    if (!before || !after) continue;
    const ids = new Set(after.map((row) => row.id));
    const known = new Set(before.map((row) => row.id));
    const added = after
      .filter((row) => !known.has(row.id) && typeof row.label === "string")
      .map((row) => (row.label as string).trim())
      .filter(Boolean);
    const removed = before.filter(
      (row) => row.brandRole === "competitor" && !ids.has(row.id) && typeof row.brandId === "string",
    );
    if (added.length === 0 && removed.length === 0) continue;
    editedKeys.push(key);
    grids.push({ key, before, after });
    typed.push(...added);
    removed.forEach((row) => leaving.add(row.brandId as string));
  }
  if (editedKeys.length === 0) return null;
  const taken = new Set(current.map((competitor) => competitor.id));
  const joining = typed
    .filter((name, at) => !roster.some((entry) => entry.label === name) && typed.indexOf(name) === at)
    .map((name) => ({ id: mintCompetitorId(taken), name }));
  const competitors = [
    ...current.filter((competitor) => derived.size > 0 || !leaving.has(competitor.id)),
    ...joining,
  ];
  if (
    competitors.length === current.length &&
    competitors.every((competitor, at) => competitor.id === current[at].id)
  ) {
    return null;
  }
  const joined = new Set(joining.map((competitor) => competitor.name));
  const kept = new Set(competitors.map((competitor) => competitor.id));
  const followedKeys = grids
    .filter(({ before, after }) => {
      const afterIds = new Set(after.map((row) => row.id));
      const beforeIds = new Set(before.map((row) => row.id));
      const keptRows = after.filter((row) => beforeIds.has(row.id));
      const savedKept = before.filter((row) => afterIds.has(row.id));
      const added = after.slice(keptRows.length);
      return (
        keptRows.length === savedKept.length &&
        keptRows.every((row, at) => row.id === savedKept[at].id && row.label === savedKept[at].label) &&
        added.every((row) => !beforeIds.has(row.id) && joined.has(String(row.label).trim())) &&
        before.every(
          (row) =>
            afterIds.has(row.id) ||
            (row.brandRole === "competitor" && !kept.has(String(row.brandId))),
        )
      );
    })
    .map(({ key }) => key);
  return { competitors, editedKeys, followedKeys };
}
